// Texas Hold'em Poker Calculator Service
// Based on Monte Carlo simulation for win rate calculation

// Card suits and ranks
const SUITS = ['♠', '♥', '♦', '♣'];
const RANKS = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];
const RANK_VALUES = {
  '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
  '10': 10, 'J': 11, 'Q': 12, 'K': 13, 'A': 14
};

// Hand rankings (higher is better)
const HAND_RANKINGS = {
  HIGH_CARD: 1,
  ONE_PAIR: 2,
  TWO_PAIR: 3,
  THREE_OF_KIND: 4,
  STRAIGHT: 5,
  FLUSH: 6,
  FULL_HOUSE: 7,
  FOUR_OF_KIND: 8,
  STRAIGHT_FLUSH: 9,
  ROYAL_FLUSH: 10
};

/**
 * Generate a full deck of 52 cards
 * @returns {Array} Array of card objects {rank, suit, value}
 */
export const generateDeck = () => {
  const deck = [];
  for (const suit of SUITS) {
    for (const rank of RANKS) {
      deck.push({
        rank,
        suit,
        value: RANK_VALUES[rank],
        id: `${rank}${suit}`
      });
    }
  }
  return deck;
};

/**
 * Compare two cards for equality
 */
export const cardsEqual = (card1, card2) => {
  return card1.rank === card2.rank && card1.suit === card2.suit;
};

/**
 * Evaluate a poker hand and return its ranking
 * @param {Array} cards - Array of 5-7 cards
 * @returns {Object} {ranking, name, value}
 */
export const evaluateHand = (cards) => {
  if (cards.length < 5) {
    return { ranking: 0, name: 'Incomplete', value: 0 };
  }

  // Get all possible 5-card combinations
  const allHands = get5CardCombinations(cards);

  // Evaluate each combination and return the best
  let bestHand = { ranking: 0, value: 0 };

  for (const hand of allHands) {
    const evaluation = evaluate5Cards(hand);
    if (evaluation.ranking > bestHand.ranking ||
        (evaluation.ranking === bestHand.ranking && evaluation.value > bestHand.value)) {
      bestHand = evaluation;
    }
  }

  return bestHand;
};

/**
 * Get all 5-card combinations from a set of cards
 */
const get5CardCombinations = (cards) => {
  if (cards.length === 5) return [cards];
  if (cards.length < 5) return [];

  const combinations = [];

  const combine = (start, chosen) => {
    if (chosen.length === 5) {
      combinations.push([...chosen]);
      return;
    }

    for (let i = start; i < cards.length; i++) {
      chosen.push(cards[i]);
      combine(i + 1, chosen);
      chosen.pop();
    }
  };

  combine(0, []);
  return combinations;
};

/**
 * Evaluate exactly 5 cards
 */
const evaluate5Cards = (cards) => {
  const sorted = [...cards].sort((a, b) => b.value - a.value);

  const ranks = sorted.map(c => c.rank);
  const suits = sorted.map(c => c.suit);
  const values = sorted.map(c => c.value);

  // Count rank occurrences
  const rankCounts = {};
  ranks.forEach(rank => {
    rankCounts[rank] = (rankCounts[rank] || 0) + 1;
  });

  const counts = Object.values(rankCounts).sort((a, b) => b - a);
  const isFlush = suits.every(suit => suit === suits[0]);
  const isStraight = checkStraight(values);

  // Check for Royal Flush
  if (isFlush && isStraight && values[0] === 14) {
    return { ranking: HAND_RANKINGS.ROYAL_FLUSH, name: '皇家同花順', value: 10000000 + values[0] };
  }

  // Check for Straight Flush
  if (isFlush && isStraight) {
    return { ranking: HAND_RANKINGS.STRAIGHT_FLUSH, name: '同花順', value: 9000000 + values[0] };
  }

  // Check for Four of a Kind
  if (counts[0] === 4) {
    return { ranking: HAND_RANKINGS.FOUR_OF_KIND, name: '四條', value: 8000000 + getHandValue(rankCounts, [4, 1]) };
  }

  // Check for Full House
  if (counts[0] === 3 && counts[1] === 2) {
    return { ranking: HAND_RANKINGS.FULL_HOUSE, name: '葫蘆', value: 7000000 + getHandValue(rankCounts, [3, 2]) };
  }

  // Check for Flush
  if (isFlush) {
    return { ranking: HAND_RANKINGS.FLUSH, name: '同花', value: 6000000 + sumValues(values) };
  }

  // Check for Straight
  if (isStraight) {
    return { ranking: HAND_RANKINGS.STRAIGHT, name: '順子', value: 5000000 + values[0] };
  }

  // Check for Three of a Kind
  if (counts[0] === 3) {
    return { ranking: HAND_RANKINGS.THREE_OF_KIND, name: '三條', value: 4000000 + getHandValue(rankCounts, [3, 1, 1]) };
  }

  // Check for Two Pair
  if (counts[0] === 2 && counts[1] === 2) {
    return { ranking: HAND_RANKINGS.TWO_PAIR, name: '兩對', value: 3000000 + getHandValue(rankCounts, [2, 2, 1]) };
  }

  // Check for One Pair
  if (counts[0] === 2) {
    return { ranking: HAND_RANKINGS.ONE_PAIR, name: '一對', value: 2000000 + getHandValue(rankCounts, [2, 1, 1, 1]) };
  }

  // High Card
  return { ranking: HAND_RANKINGS.HIGH_CARD, name: '高牌', value: 1000000 + sumValues(values) };
};

/**
 * Check if values form a straight
 */
const checkStraight = (values) => {
  // Normal straight
  for (let i = 0; i < values.length - 1; i++) {
    if (values[i] - values[i + 1] !== 1) {
      // Check for A-2-3-4-5 (wheel)
      if (!(values[0] === 14 && values[1] === 5 && values[2] === 4 && values[3] === 3 && values[4] === 2)) {
        return false;
      }
    }
  }
  return true;
};

/**
 * Get hand value for comparison
 */
const getHandValue = (rankCounts, pattern) => {
  const sortedRanks = Object.entries(rankCounts)
    .sort((a, b) => {
      if (b[1] !== a[1]) return b[1] - a[1];
      return RANK_VALUES[b[0]] - RANK_VALUES[a[0]];
    });

  let value = 0;
  sortedRanks.forEach((entry, idx) => {
    value += RANK_VALUES[entry[0]] * Math.pow(100, pattern.length - idx - 1);
  });

  return value;
};

/**
 * Sum card values
 */
const sumValues = (values) => {
  return values.reduce((sum, val, idx) => sum + val * Math.pow(100, 4 - idx), 0);
};

/**
 * Calculate win rates using Monte Carlo simulation
 * @param {Array} players - Array of player hands [{cards: [card1, card2]}]
 * @param {Array} communityCards - Community cards (0-5)
 * @param {Number} simulations - Number of simulations to run
 * @returns {Object} Win rates for each player
 */
export const calculateWinRates = (players, communityCards = [], simulations = 10000) => {
  if (players.length < 2) {
    throw new Error('至少需要 2 位玩家');
  }

  // Create deck and remove known cards
  const deck = generateDeck();
  const usedCards = [...communityCards];
  players.forEach(player => {
    if (player.cards) {
      usedCards.push(...player.cards);
    }
  });

  const availableCards = deck.filter(card =>
    !usedCards.some(used => cardsEqual(card, used))
  );

  // Track wins for each player
  const wins = new Array(players.length).fill(0);
  const ties = new Array(players.length).fill(0);

  // Run simulations
  for (let i = 0; i < simulations; i++) {
    const shuffled = [...availableCards].sort(() => Math.random() - 0.5);
    let cardIndex = 0;

    // Deal missing community cards
    const fullCommunity = [...communityCards];
    while (fullCommunity.length < 5) {
      fullCommunity.push(shuffled[cardIndex++]);
    }

    // Deal missing hole cards and evaluate hands
    const evaluations = players.map(player => {
      const playerCards = player.cards ? [...player.cards] : [];

      // Deal missing hole cards
      while (playerCards.length < 2) {
        playerCards.push(shuffled[cardIndex++]);
      }

      // Evaluate hand
      const allCards = [...playerCards, ...fullCommunity];
      return evaluateHand(allCards);
    });

    // Find winner(s)
    const maxRanking = Math.max(...evaluations.map(e => e.ranking));
    const winners = evaluations
      .map((e, idx) => ({ idx, ...e }))
      .filter(e => e.ranking === maxRanking);

    if (winners.length === 1) {
      // Single winner
      wins[winners[0].idx]++;
    } else {
      // Multiple players with same ranking - compare values
      const maxValue = Math.max(...winners.map(w => w.value));
      const finalWinners = winners.filter(w => w.value === maxValue);

      if (finalWinners.length === 1) {
        wins[finalWinners[0].idx]++;
      } else {
        // Tie
        finalWinners.forEach(w => {
          ties[w.idx]++;
        });
      }
    }
  }

  // Calculate percentages
  return players.map((player, idx) => ({
    player: idx + 1,
    wins: wins[idx],
    ties: ties[idx],
    losses: simulations - wins[idx] - ties[idx],
    winRate: ((wins[idx] / simulations) * 100).toFixed(2),
    tieRate: ((ties[idx] / simulations) * 100).toFixed(2),
    totalRate: (((wins[idx] + ties[idx]) / simulations) * 100).toFixed(2)
  }));
};

/**
 * Get card display name
 */
export const getCardDisplay = (card) => {
  return `${card.rank}${card.suit}`;
};

/**
 * Parse card string to card object
 */
export const parseCard = (cardString) => {
  const deck = generateDeck();
  return deck.find(card => card.id === cardString);
};
